// Copyright 2024 Sorama B.V.
import toastr from "toastr";

// Toast error that enables a title to be set
export interface IToastError extends Error {
    title?: string;
}
// Enable type checking for the error
function isToastError(error): error is IToastError {
    return error.title !== undefined;
}

/**
 * A class to display toast messages
 * Wraps the toastr library
 */
export class Toast {
    /**
     * Initialize the toastr options for a generic toast
     * This should be called before any other method as the default options might have been changed
     */
    public static init(): void {
        toastr.options = {
            closeButton: false,
            debug: false,
            newestOnTop: false,
            progressBar: false,
            positionClass: "toast-bottom-left",
            preventDuplicates: false,
            onclick: null,
            showDuration: "3000", // After hovering, how long it takes to hide (in ms)
            hideDuration: "1000",
            timeOut: "5000", // How long the toast is visible (in ms)
            extendedTimeOut: "1000",
            showEasing: "swing",
            hideEasing: "linear",
            showMethod: "fadeIn",
            hideMethod: "fadeOut",
        };
    }

    /**
     * Set the target element for the toast to be attached to
     * @param targetClass The id of the element to which the toast should be attached
     */
    private static setTargetElement(targetId: string): void {
        if (targetId) {
            toastr.options.positionClass = "localToast";
            toastr.options.target = "#" + targetId;
        }
    }

    /**
     * Show a success toast
     * @param message  The message to display
     * @param title   The title of the toast
     * @param targetId  The id of the element to which the toast should be attached
     */
    public static success(message: string, title?: string, targetId?: string): void {
        this.init();
        this.setTargetElement(targetId);

        toastr.success(message, title);
    }

    /**
     * Show an info toast
     * @param message  The message to display
     * @param title   The title of the toast
     * @param targetId  The id of the element to which the toast should be attached
     */
    public static info(message: string, title?: string, targetId?: string): void {
        this.init();
        this.setTargetElement(targetId);

        toastr.info(message, title);
    }

    /**
     * Show a warning toast
     * @param message  The message to display
     * @param title   The title of the toast
     * @param targetId  The id of the element to which the toast should be attached
     */
    public static warning(message: string, title?: string, targetId?: string): void {
        this.init();
        this.setTargetElement(targetId);

        toastr.warning(message, title);
    }

    /**
     * Show an error toast
     * @param message  The message to display
     * @param title   The title of the toast
     * @param targetId  The id of the element to which the toast should be attached
     */
    public static error(message: string, title?: string, targetId?: string): void {
        this.init();
        this.setTargetElement(targetId);

        toastr.options.closeButton = true;
        toastr.options.tapToDismiss = false;

        toastr.error(message, title);
    }

    /**
     * General function to display an error anywhere in the code.
     *
     * @param error
     * @param targetId
     */
    public static displayError(error: unknown, targetId?: string): void {
        if (isToastError(error)) {
            Toast.error(error.message, error.title, targetId);
        } else if (error instanceof Error) {
            Toast.error(error.message, "Error", targetId);
        } else if (typeof error === "string") {
            Toast.error(error, "Error", targetId);
        } else {
            Toast.error("An unknown error occurred", "Error", targetId);
        }
    }
}
